import { useEffect, useState, createContext, useContext, useCallback } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';
import LandingPage from './pages/LandingPage';
import MarketplacePage from './pages/MarketplacePage';
import ListingDetailPage from './pages/ListingDetailPage';
import StorefrontPage from './pages/StorefrontPage';
import AuthPage from './pages/AuthPage';
import AuthCallback from './pages/AuthCallback';
import SupplierDashboard from './pages/SupplierDashboard';
import CreateListingPage from './pages/CreateListingPage';
import BulkImportPage from './pages/BulkImportPage';
import ProfilePage from './pages/ProfilePage';
import AlertsPage from './pages/AlertsPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import FavoritesPage from './pages/FavoritesPage';
import TradeExchangePage from './pages/TradeExchangePage';
import AboutPage from './pages/AboutPage';
import InspirationPage from './pages/InspirationPage';
import SuppliersPage from './pages/SuppliersPage';
import AdminInspirationsPage from './pages/AdminInspirationsPage';
import AdminClipPage from './pages/AdminClipPage';
import AdminSuppliersPage from './pages/AdminSuppliersPage';
import AdminImportsPage from './pages/AdminImportsPage';
import AdminReportsPage from './pages/AdminReportsPage';
import SourcingPage from './pages/SourcingPage';
import SourcingCallDetailPage from './pages/SourcingCallDetailPage';
import CallUsWidget from './components/CallUsWidget';
import SustainabilityReportPage from './pages/SustainabilityReportPage';
import SupplierAppPage from './pages/SupplierAppPage';
import TrustPage from './pages/TrustPage';
import RevitDownloadPage from './pages/RevitDownloadPage';
import SketchUpDownloadPage from './pages/SketchUpDownloadPage';
import StyleGuidePage from './pages/StyleGuidePage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import SharedProjectPage from './pages/SharedProjectPage';
import WingmanLayout from './wingman/WingmanLayout';
import WingmanPairPage from './wingman/WingmanPairPage';
import WingmanInboxPage from './wingman/WingmanInboxPage';
import WingmanQuickAddPage from './wingman/WingmanQuickAddPage';
import WingmanSettingsPage from './wingman/WingmanSettingsPage';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import { FavoritesProvider } from './context/FavoritesContext';
import { devLog, devError, devWarn } from './utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Send cookies on every cross-origin request — required for httpOnly auth cookies.
axios.defaults.withCredentials = true;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Token kept in memory only. The real source of truth is the httpOnly
  // `access_token` cookie set by the backend on login/register — automatically
  // sent on every request because of axios.defaults.withCredentials = true.
  const [token, setToken] = useState(null);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      // The httpOnly cookie is sent automatically; no Authorization header needed.
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // 401 = not logged in. Don't log this as an error — it's the normal
      // first-visit case before the user has authenticated.
      if (error?.response?.status !== 401) {
        devError('Auth check failed:', error);
      }
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API}/auth/login`, { email, password, remember_me: rememberMe });
    const { access_token, user: userData } = response.data;
    // Backend already set the httpOnly cookie. We keep the token in memory
    // for legacy `Authorization: Bearer ...` calls in pages we haven't migrated yet.
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (err) {
      // Even if the server call fails (network/CORS/etc.), we still want
      // to clear local state below — log so the failure isn't silent.
      devWarn('Logout request failed; clearing local session anyway:', err?.message);
    }
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, token, login, register, logout, updateUser, setUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

// NOTE: Subdomain-based storefronts (e.g. federation-trading.patinamart.com)
// are NOT supported on Emergent's deployment platform — wildcard routing is
// unavailable. We use path-based storefronts instead: /s/{slug}. The detect /
// reserved-list helpers were removed when this was confirmed (Apr 2026).

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Wingman is a standalone PWA shell — no Navbar/Footer.
  const isWingman = location.pathname.startsWith('/wingman');
  if (isWingman) {
    return (
      <Routes>
        <Route path="/wingman/pair" element={<WingmanPairPage />} />
        <Route path="/wingman" element={<WingmanLayout />}>
          <Route index element={<WingmanInboxPage />} />
          <Route path="inbox" element={<WingmanInboxPage />} />
          <Route path="quick-add" element={<WingmanQuickAddPage />} />
          <Route path="settings" element={<WingmanSettingsPage />} />
        </Route>
      </Routes>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/inspiration" element={<InspirationPage />} />
          <Route path="/suppliers" element={<SuppliersPage />} />
          <Route path="/admin/inspirations" element={<AdminInspirationsPage />} />
          <Route path="/admin/clip" element={<AdminClipPage />} />
          <Route path="/admin/suppliers" element={<AdminSuppliersPage />} />
          <Route path="/admin/imports" element={<AdminImportsPage />} />
          <Route path="/admin/reports" element={<AdminReportsPage />} />
          <Route path="/sourcing" element={<SourcingPage />} />
          <Route path="/sourcing/:call_id" element={<SourcingCallDetailPage />} />
          <Route path="/sustainability-report" element={<SustainabilityReportPage />} />
          <Route path="/style-guide" element={
            <ProtectedRoute requiredType="admin">
              <StyleGuidePage />
            </ProtectedRoute>
          } />
          <Route path="/listing/:id" element={<ListingDetailPage />} />
          <Route path="/s/:slug" element={<StorefrontPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/register" element={<AuthPage />} />
          <Route path="/dashboard" element={
            <ProtectedRoute requiredType="supplier">
              <SupplierDashboard />
            </ProtectedRoute>
          } />
          <Route path="/create-listing" element={
            <ProtectedRoute requiredType="supplier">
              <CreateListingPage />
            </ProtectedRoute>
          } />
          <Route path="/edit-listing/:id" element={
            <ProtectedRoute requiredType="supplier">
              <CreateListingPage />
            </ProtectedRoute>
          } />
          <Route path="/bulk-import" element={
            <ProtectedRoute requiredType="supplier">
              <BulkImportPage />
            </ProtectedRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/alerts" element={
            <ProtectedRoute>
              <AlertsPage />
            </ProtectedRoute>
          } />
          <Route path="/favorites" element={
            <ProtectedRoute>
              <FavoritesPage />
            </ProtectedRoute>
          } />
          <Route path="/trade-exchange" element={
            <ProtectedRoute>
              <TradeExchangePage />
            </ProtectedRoute>
          } />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/supplier-app" element={<SupplierAppPage />} />
          <Route path="/trust" element={<TrustPage />} />
          <Route path="/revit" element={<RevitDownloadPage />} />
          <Route path="/sketchup" element={<SketchUpDownloadPage />} />
          <Route path="/projects" element={
            <ProtectedRoute>
              <ProjectsPage />
            </ProtectedRoute>
          } />
          <Route path="/projects/:projectId" element={
            <ProtectedRoute>
              <ProjectDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/projects/shared/:token" element={<SharedProjectPage />} />
          <Route path="/payment/success" element={
            <ProtectedRoute>
              <PaymentSuccessPage />
            </ProtectedRoute>
          } />
        </Routes>
      </main>
      <Footer />
      <CallUsWidget />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FavoritesProvider>
          <Toaster position="top-right" richColors />
          <ScrollToTop />
          <AppRouter />
        </FavoritesProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
